import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  UserCredential,
  Unsubscribe,
  User as FirebaseUser
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { AuthRepository, AuthResult, AuthUser } from '../../domain/repositories/AuthRepository';
import { AuthenticationError } from '../../domain/errors';
import { ILoggerService } from '../../domain/services/LoggerService';

class FirebaseAuthUser implements AuthUser {
  constructor(private firebaseUser: FirebaseUser) {}

  get uid(): string {
    return this.firebaseUser.uid;
  }

  get email(): string {
    return this.firebaseUser.email || '';
  }
}

export class FirebaseAuthAdapter implements AuthRepository {
  private readonly logger: ILoggerService;

  constructor(logger: ILoggerService) {
    this.logger = logger;
  }

  async signInWithCredentials(email: string, password: string): Promise<AuthResult> {
    const startTime = Date.now();
    this.logger.logRepositoryRequest('AuthRepository', 'signInWithCredentials', { email });
    
    try {
      const userCredential: UserCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      const result = {
        uid: firebaseUser.uid,
        email: firebaseUser.email || ''
      };

      const duration = Date.now() - startTime;
      this.logger.logRepositoryResponse('AuthRepository', 'signInWithCredentials', duration, { 
        uid: result.uid, 
        email: result.email
      });

      return result;
    } catch (error: any) {
      const duration = Date.now() - startTime;
      this.logger.logRepositoryError('AuthRepository', 'signInWithCredentials', duration, error);
      throw new AuthenticationError("SIGN_IN_FAILED");
    }
  }

  async signOut(): Promise<void> {
    const startTime = Date.now();
    this.logger.logRepositoryRequest('AuthRepository', 'signOut', {});
    
    try {
      await signOut(auth);
      
      const duration = Date.now() - startTime;
      this.logger.logRepositoryResponse('AuthRepository', 'signOut', duration, { success: true });
    } catch (error: any) {
      const duration = Date.now() - startTime;
      this.logger.logRepositoryError('AuthRepository', 'signOut', duration, error);
      throw new AuthenticationError("SIGN_OUT_FAILED");
    }
  }

  getCurrentAuthUser(): AuthUser | null {
    const firebaseUser = auth.currentUser;
    return firebaseUser ? new FirebaseAuthUser(firebaseUser) : null;
  }

  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    const unsubscribe: Unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      const authUser = firebaseUser ? new FirebaseAuthUser(firebaseUser) : null;
      callback(authUser);
    });

    return unsubscribe;
  }
}